<!-- DefaultCommentsTable.vue -->
<script lang="ts" setup>
    import { computed, reactive, onMounted } from 'vue';
    import * as ChoiceApi from '../../lib/ChoiceApi';
    import { ICol } from '../../lib/ChoiceTableHelpers';
    import { DisplayStyle, IconTypes } from '../../Enums/UiEnums';
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import Icon from '../../components/Icon.vue';
    import DropDownButton from "../../components/DropDownButton.vue";
    import DropDownButtonItem from "../../components/DropDownButtonItem.vue";
    import DropDownButtonDivider from "../../components/DropDownButtonDivider.vue";
    import LoadingSection from '../../components/LoadingSection.vue';
    import DefaultCommentEditor from './DefaultCommentEditor.vue'
    import { useDefaultCommentsStore } from '../../lib/Stores/DefaultCommentsStore'
    import { initPinia } from "../../lib/PiniaMgr"
    import { IDefaultCommentModel } from '../../lib/Api/Comments';

    const useDefaultActionButton = "useDefaultActionButton";

    const emit = defineEmits<{
        (e: "selected", defaultComment: IDefaultCommentModel, overwrite?: boolean | undefined): void
    }>();

    const props = defineProps<{
        initialConstantGroupId?: number,
        showClaimCommentParts?: boolean
    }>();

    const defaultCommentsStore = useDefaultCommentsStore(initPinia());

    onMounted(async ()=>{
        if (props.initialConstantGroupId)
        {
            // If there is a suggested initial (constant) group ID, get the database group ID
            const defaultCommentGroupId = (await defaultCommentsStore.commentGroupByConstantId(props.initialConstantGroupId));
            compData.defaultCommentGroupId = defaultCommentGroupId?.dflt_comment_group_id;
        }

        // See if the groups are available externally
        await defaultCommentsStore.initializeDefaultGroups();

        // See if the comments are available externally
        await defaultCommentsStore.initializeDefaultComments();
    });

    const compData = reactive<{
        defaultCommentTableColumns: ICol[],
        defaultCommentGroupId?: number,
        commentsEditor:
        {
            show: boolean,
            commentId?: number
        }
    }>({
        defaultCommentTableColumns: [
            {Name: "", DataField: "dflt_comment_id", ExtraData: useDefaultActionButton, HideFilter: true },
            {
                Name: "Group", 
                DataField: "dflt_comment_group_id", 
                FormatBodyValue: (value) => groupNameByCommentGroupId(value)
            },
            {Name: "Description", DataField: "description" },
            {Name: "Text", DataField: "commentText" },
        ],
        commentsEditor: {
            show: false
        }
    });

    const filteredGroupName = computed<string>(() => {
        const grp = defaultCommentsStore.defaultCommentGroups
                        ?.find(grp => grp.dflt_comment_group_id === compData.defaultCommentGroupId);

        return grp?.description ?? "All";
    });

    function addNewComment()
    {
        compData.commentsEditor.commentId = undefined;
        compData.commentsEditor.show = true;
    }

    const visibleDefaultComments = computed(() =>{
        if (!compData.defaultCommentGroupId)
            return defaultCommentsStore.defaultComments;

        return defaultCommentsStore.defaultComments
                ?.filter(comment => comment.dflt_comment_group_id === compData.defaultCommentGroupId);
    });

    function commentSelected(commentId: number, overwrite: boolean)
    {
        const comment = defaultCommentsStore.defaultComments?.find(i => i.dflt_comment_id === commentId);
        if (!comment)
        {
            alert (`Unable to find matching comment with an ID of ${commentId}`);
            return;
        }
        emit("selected", comment, overwrite);
    }

    function editComment(commentId: number)
    {
        compData.commentsEditor.commentId = commentId;
        compData.commentsEditor.show = true;
    }

    function groupNameByCommentGroupId(dflt_comment_group_id: number): string|undefined
    {
        return defaultCommentsStore.defaultCommentGroups?.find(i => i.dflt_comment_group_id == dflt_comment_group_id)?.description;
    }

    function defaultCommentSaved(defaultComment: IDefaultCommentModel, inserted: boolean)
    {
        compData.commentsEditor.show = false;
        if (inserted)
        {
            emit("selected", defaultComment);
        }
    }
</script>

<template>
    <div v-if="!compData.commentsEditor.show">
        <div>
            <LoadingSection :isLoading="!defaultCommentsStore.defaultCommentGroups?.length" loadingMsg="Loading Groups">
                <div class="float-end mb-1">
                    <button class="btn btn-primary me-1" @click="addNewComment()">
                        <Icon :icon="IconTypes.Add" :displayStyle="DisplayStyle.Light"/>
                    </button>

                    <DropDownButton title="Shows a library of default comments" class="mb-1">
                        <DropDownButtonItem @click="compData.defaultCommentGroupId = undefined">
                            All
                        </DropDownButtonItem>
                        <DropDownButtonDivider/>
                        <DropDownButtonItem 
                                v-for="commentGroup in defaultCommentsStore.defaultCommentGroups ?? []" 
                                @click="compData.defaultCommentGroupId = commentGroup.dflt_comment_group_id">
                        {{ commentGroup.description }}
                        </DropDownButtonItem>

                        <template #button>
                            <span class="me-2">
                                Filtered By: {{ filteredGroupName }}
                            </span>
                        </template>    
                    </DropDownButton>
                </div>
            </LoadingSection>
        </div>

        <LoadingSection :isLoading="!defaultCommentsStore.defaultComments?.length" loadingMsg="Loading Default Comments">
            <!-- Default Comments -->
            <ChoiceTable 
                :showFilters="true"
                :allowSorting="true"
                keyValueDataMember="dflt_comment_id"
                :columns="compData.defaultCommentTableColumns" 
                :data="visibleDefaultComments">
                <template #cell="{col, value}">
                    <template v-if="col.ExtraData === useDefaultActionButton">
                        <div style="white-space:nowrap">
                            <button class="btn btn-primary btn-sm" @click="commentSelected(value, false)" title="Append this comment text to the current comment">
                                <Icon :icon="IconTypes.Add" :displayStyle="DisplayStyle.Light" />
                            </button>
                            <button class="btn btn-primary btn-sm ms-1" @click="commentSelected(value, true)" title="Replace comment with this comment text">
                                <Icon :icon="IconTypes.TurnLeft" :displayStyle="DisplayStyle.Light" />
                            </button>
                            <button class="btn btn-primary btn-sm ms-1" title="Edit comment" @click="editComment(value)">
                                <Icon :icon="IconTypes.Edit" :displayStyle="DisplayStyle.Light"/>
                            </button>
                        </div>
                    </template>
                </template>
            </ChoiceTable>
        </LoadingSection>
    </div>

    <div v-if="compData.commentsEditor.show">
        <DefaultCommentEditor 
            :comment-id="compData.commentsEditor.commentId" 
            :defaultCommentGroupId="compData.defaultCommentGroupId"
            :showClaimCommentParts="props.showClaimCommentParts"
            @cancel="compData.commentsEditor.show = false"
            @saved="defaultCommentSaved" />
    </div>

</template>